import React, { useCallback } from "react";
import { Alert, Input, InputNumber } from "antd";
import { validateIpAString } from "../../../utils/utils";
// COMPONENTS
import OpcUaFieldLabel from "../../edgeManagement/adapters/CDO/OPCUA/OpcUaFieldLabel";

// ************************************************ */
// SMTP TYPES ************************************* */
// Outbound mail settings used for offline email notifications. Field names
// follow the Servers table columns (smtp_host, smtp_port, smtp_user, smtp_pass,
// smtp_from) so the request body can carry them without renaming once the
// endpoint is wired.

export interface SmtpForm {
  host: string;
  port: number | null;
  user: string;
  pass: string;
  from: string;
}

export const emptySmtpForm = (): SmtpForm => ({
  host: "",
  port: null,
  user: "",
  pass: "",
  from: "",
});

/**
 * Whether the operator has entered any SMTP value.
 *
 * The section is optional: a server without offline email notifications is
 * valid. Once any field has content, the required fields are checked by
 * smtpProblems.
 */
export const smtpHasContent = (form: SmtpForm): boolean =>
  form.host.trim() !== "" ||
  form.port !== null ||
  form.user.trim() !== "" ||
  form.pass !== "" ||
  form.from.trim() !== "";

const HOSTNAME_PATTERN =
  /^(?=.{1,253}$)[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Problems that would make the notification relay unusable.
 *
 * Checked as the operator types so a bad host or sender is caught here, not
 * after the edge server has failed to send its first alert.
 */
export const smtpProblems = (form: SmtpForm): string[] => {
  if (!smtpHasContent(form)) return [];

  const problems: string[] = [];
  const host = form.host.trim();

  if (!host) {
    problems.push("SMTP host is required.");
  } else if (!validateIpAString(host) && !HOSTNAME_PATTERN.test(host)) {
    problems.push("SMTP host must be a hostname or an IP address, without a scheme or port.");
  }

  if (form.port === null || form.port < 1 || form.port > 65535) {
    problems.push("SMTP port must be between 1 and 65535.");
  }

  const from = form.from.trim();
  if (!from) {
    problems.push("Sender address is required.");
  } else if (!EMAIL_PATTERN.test(from)) {
    problems.push("Sender address must be an email address.");
  }

  if (form.pass !== "" && form.user.trim() === "") {
    problems.push("A password needs a username.");
  }

  return problems;
};

// ************************************************ */
// COMPONENT ************************************** */

interface Props {
  value: SmtpForm;
  onChange: (next: SmtpForm) => void;
  disabled?: boolean;
}

function ServerSmtpForm({ value, onChange, disabled = false }: Props) {
  const patch = useCallback(
    <K extends keyof SmtpForm>(key: K, next: SmtpForm[K]) => {
      onChange({ ...value, [key]: next });
    },
    [onChange, value],
  );

  const problems = smtpProblems(value);

  return (
    <div>
      {problems.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: "16px" }}
          message={`${problems.length} problem${problems.length > 1 ? "s" : ""} in the SMTP settings`}
          description={
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          }
        />
      )}

      {/* Row 1: username, full width */}
      <div className="row">
        <div className="col-12 mt-2">
          <OpcUaFieldLabel
            label="Username"
            tooltip="Account the relay authenticates with. Leave blank if the relay accepts mail from this network without a login."
          />
          <Input
            className="generalStyles__input"
            size="large"
            id="smtp-user"
            aria-label="Username"
            placeholder="Relay login, often the mailbox address"
            autoComplete="off"
            style={{ marginTop: 8 }}
            value={value.user}
            disabled={disabled}
            onChange={(e) => patch("user", e.target.value)}
          />
        </div>
      </div>

      {/* Row 2: sender address and password */}
      <div className="row">
        <div className="col-12 col-md-6 mt-3">
          <OpcUaFieldLabel
            label="Sender address"
            tooltip="Address the notification emails are sent from. Most relays reject mail whose sender is not a mailbox the account is allowed to send as."
          />
          <Input
            className="generalStyles__input"
            size="large"
            id="smtp-from"
            aria-label="Sender address"
            placeholder="alerts@site.example"
            style={{ marginTop: 8 }}
            value={value.from}
            disabled={disabled}
            onChange={(e) => patch("from", e.target.value)}
          />
        </div>
        <div className="col-12 col-md-6 mt-3">
          <OpcUaFieldLabel
            label="Password"
            tooltip="Password for the username above. It is not kept in the wizard draft, so it must be entered again after a reload."
          />
          <Input.Password
            className="generalStyles__input"
            size="large"
            id="smtp-pass"
            aria-label="Password"
            autoComplete="new-password"
            style={{ marginTop: 8 }}
            value={value.pass}
            disabled={disabled}
            onChange={(e) => patch("pass", e.target.value)}
          />
        </div>
      </div>

      {/* Row 3: relay host and port */}
      <div className="row">
        <div className="col-12 col-md-6 mt-3">
          <OpcUaFieldLabel
            label="SMTP host"
            tooltip="Hostname or IP address of the outgoing mail relay, for example smtp.office365.com. Enter the host only, without a scheme or port."
          />
          <Input
            className="generalStyles__input"
            size="large"
            id="smtp-host"
            aria-label="SMTP host"
            placeholder="smtp.office365.com"
            style={{ marginTop: 8 }}
            value={value.host}
            disabled={disabled}
            onChange={(e) => patch("host", e.target.value)}
          />
        </div>
        <div className="col-12 col-md-6 mt-3">
          <OpcUaFieldLabel
            label="SMTP port"
            tooltip="Port of the relay. 587 uses STARTTLS and is the usual choice for authenticated sending. Port 25 is for relay-only setups and is often blocked by ISPs."
          />
          <InputNumber
            className="generalStyles__input"
            size="large"
            id="smtp-port"
            aria-label="SMTP port"
            style={{ display: "block", width: "100%", marginTop: 8 }}
            placeholder="587"
            value={value.port}
            min={1}
            max={65535}
            precision={0}
            disabled={disabled}
            onChange={(v) => patch("port", v)}
          />
        </div>
      </div>
    </div>
  );
}

export default ServerSmtpForm;
